/**
 * StepInput.tsx  (Step 1)
 *
 * The entry screen: user types a raw thought and clicks "Distill →".
 * Pure presentational component — all state and the distill handler live in
 * the parent orchestrator (index.tsx).
 */

import React, { useEffect, useRef, useState } from "react";
import { card, primaryBtn, stepHeading, stepSub, textarea, ACCENT } from "./sharedStyles";
import SlideCountPicker from "./SlideCountPicker";
import { MicInput } from "../../components/MicInput";

// ── Animated nudge ────────────────────────────────────────────────────────────

const NUDGE_MESSAGE = "You — posts with a personal example get 2–3× more engagement";
const NUDGE_DELAY_MS = 1500;

/**
 * Typewriter nudge that appears below the textarea 1.5 s after the user starts
 * typing. Invisible when the textarea is empty. No new field — just attention.
 * Rollback: remove this hook + the <NudgeHint> usage below.
 */
function useNudge(thought: string): string {
  const [displayed, setDisplayed] = useState("");
  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const firedRef  = useRef(false);

  useEffect(() => {
    if (thought.length === 0) {
      // Reset when textarea cleared
      firedRef.current = false;
      setDisplayed("");
      if (timerRef.current)  clearTimeout(timerRef.current);
      if (frameRef.current)  clearInterval(frameRef.current);
      return;
    }

    if (firedRef.current) return; // already counting / already done
    firedRef.current = true;

    timerRef.current = setTimeout(() => {
      let i = 0;
      frameRef.current = setInterval(() => {
        i += 1;
        setDisplayed(NUDGE_MESSAGE.slice(0, i));
        if (i >= NUDGE_MESSAGE.length) {
          clearInterval(frameRef.current!);
        }
      }, 22); // ~22 ms per char → ~650 ms total for the full message
    }, NUDGE_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (frameRef.current) clearInterval(frameRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thought.length === 0]);

  return displayed;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  thought:               string;
  isDistilling:          boolean;
  distillError:          string | null;
  preferredSlideCount:   number | null;
  setThought:            (v: string) => void;
  setPreferredSlideCount:(v: number | null) => void;
  onDistill:             () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const StepInput: React.FC<Props> = ({
  thought, isDistilling, distillError,
  preferredSlideCount, setPreferredSlideCount,
  setThought, onDistill,
}) => {
  const canSubmit  = thought.trim().length > 0 && !isDistilling;
  const nudgeText  = useNudge(thought);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div>
        <p style={stepHeading}>What's your raw thought?</p>
        <p style={stepSub}>
          Drop a rough idea — we'll distill it into an insight and write your LinkedIn post.
        </p>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <textarea
          data-testid="v2-thought-input"
          style={{ ...textarea, padding: "20px", minHeight: "120px" }}
          placeholder="e.g. most founders confuse being busy with making progress…"
          value={thought}
          onChange={e => setThought(e.target.value)}
          onKeyDown={e => {
            // Allow Shift+Enter for new lines; plain Enter submits
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSubmit) onDistill();
            }
          }}
          autoFocus
        />

        <div style={{
          padding:    "12px 16px",
          borderTop:  "1px solid #E4E4E0",
          display:    "flex",
          alignItems: "center",
          gap:        "12px",
        }}>
          {/* Mic input — appends transcript to whatever the user has typed.
              The reusable component handles permissions, recording, upload, and errors. */}
          <MicInput
            size="md"
            disabled={isDistilling}
            ariaLabel="Record your thought"
            onTranscript={text => {
              const trimmed = text.trim();
              if (!trimmed) return;
              // Append (don't replace) so users can mix typing and dictation.
              setThought(thought ? `${thought.trim()} ${trimmed}` : trimmed);
            }}
          />

          {distillError && (
            <span style={{ fontSize: "13px", color: "#c0392b", flex: 1 }}>⚠ {distillError}</span>
          )}

          <button
            style={{
              ...primaryBtn,
              marginLeft: "auto",
              opacity: canSubmit ? 1 : 0.5,
              cursor:  canSubmit ? "pointer" : "not-allowed",
            }}
            onClick={onDistill}
            disabled={!canSubmit}
          >
            {isDistilling ? "Distilling…" : "Distill →"}
          </button>
        </div>
      </div>

      {/* ── Engagement nudge — typewriter, appears 1.5 s after first keystroke ── */}
      {nudgeText && (
        <div style={{
          fontSize:   "12px",
          color:      "#888",
          lineHeight: 1.5,
          minHeight:  "18px",
          paddingLeft: "2px",
        }}>
          <span style={{ color: ACCENT, fontWeight: 700 }}>✦</span>
          {" "}{nudgeText}
          {/* Blinking cursor while still typing */}
          {nudgeText.length < NUDGE_MESSAGE.length && (
            <span style={{
              display:    "inline-block",
              width:      "1px",
              height:     "13px",
              background: ACCENT,
              marginLeft: "2px",
              verticalAlign: "text-bottom",
              animation: "blink 0.7s step-end infinite",
            }} />
          )}
        </div>
      )}

      <SlideCountPicker value={preferredSlideCount} onChange={setPreferredSlideCount} />
    </div>
  );
};

export default StepInput;
